import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, Search, LogOut, Shield, Package, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const NavItems = ({ onClick }) => (
  <>
    <NavLink to="/" end onClick={onClick} data-testid="nav-home"
      className={({isActive}) => `text-sm font-bold uppercase tracking-wider transition-colors ${isActive ? "text-sky-500" : "text-slate-700 hover:text-sky-500"}`}>Home</NavLink>
    <NavLink to="/shop?category=girls" onClick={onClick} data-testid="nav-girls"
      className="text-sm font-bold uppercase tracking-wider text-slate-700 hover:text-pink-500 transition-colors">Girls</NavLink>
    <NavLink to="/shop?category=boys" onClick={onClick} data-testid="nav-boys"
      className="text-sm font-bold uppercase tracking-wider text-slate-700 hover:text-sky-500 transition-colors">Boys</NavLink>
    <NavLink to="/shop" onClick={onClick} data-testid="nav-shop"
      className="text-sm font-bold uppercase tracking-wider text-slate-700 hover:text-sky-500 transition-colors">All</NavLink>
    <NavLink to="/about" onClick={onClick} data-testid="nav-about"
      className="text-sm font-bold uppercase tracking-wider text-slate-700 hover:text-sky-500 transition-colors">About</NavLink>
  </>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [mobileSearch, setMobileSearch] = useState(false);

  const submitSearch = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const t = term.trim();
    navigate(t ? `/shop?q=${encodeURIComponent(t)}` : "/shop");
    setMobileSearch(false);
    if (typeof document !== "undefined" && document.activeElement) document.activeElement.blur();
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
          <img src="/assets/shine-logo.png" alt="Shine Fabric" className="h-9 sm:h-10 w-auto"/>
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          <NavItems />
        </nav>
        <div className="flex items-center gap-2">
          {/* Desktop inline search */}
          <form onSubmit={submitSearch} className="hidden md:flex items-center relative" data-testid="navbar-search-form">
            <button type="submit" aria-label="Search" data-testid="search-btn"
              className="absolute left-3 text-slate-400 hover:text-pink-500 transition-colors">
              <Search className="w-4 h-4" />
            </button>
            <input
              type="text"
              data-testid="navbar-search-input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search products…"
              className="w-56 lg:w-72 h-9 pl-9 pr-4 rounded-full bg-white border border-slate-200 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100 transition-all"
            />
          </form>
          {/* Mobile search toggle */}
          <button onClick={() => setMobileSearch((v) => !v)} aria-label="Search" data-testid="search-btn-mobile"
            className="md:hidden rounded-full w-10 h-10 flex items-center justify-center hover:bg-slate-100 transition-colors">
            <Search className="w-5 h-5" />
          </button>
          <button onClick={() => navigate("/wishlist")} data-testid="wishlist-nav"
            className="relative rounded-full w-10 h-10 flex items-center justify-center hover:bg-slate-100 transition-colors">
            <Heart className={`w-5 h-5 ${wishCount > 0 ? "text-pink-500 fill-pink-500" : ""}`}/>
            {wishCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-pink-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">{wishCount}</span>
            )}
          </button>
          <button onClick={() => setDrawerOpen(true)} data-testid="cart-btn"
            className="relative rounded-full w-10 h-10 flex items-center justify-center hover:bg-slate-100 transition-colors">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-pink-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center" data-testid="cart-count">{count}</span>
            )}
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="rounded-full w-10 h-10 brand-gradient-sky flex items-center justify-center text-white font-bold">
                  {user.picture ? <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" /> : (user.name?.[0] || "U")}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl">
                <DropdownMenuLabel className="font-display">Hi, {user.name?.split(" ")[0]}!</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account"><User className="w-4 h-4 mr-2"/>My Account</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders"><Package className="w-4 h-4 mr-2"/>My Orders</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="w-4 h-4 mr-2"/>Admin Dashboard</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2"/>Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => navigate("/login")} data-testid="login-btn"
              className="rounded-full brand-gradient-pink text-white border-0 hover:opacity-90 btn-bouncy hidden sm:inline-flex">
              Login
            </Button>
          )}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="mobile-menu">
                <Menu className="w-5 h-5"/>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader><SheetTitle className="font-display">Menu</SheetTitle></SheetHeader>
              <div className="flex flex-col gap-5 mt-8">
                <NavItems />
                {!user && <Button onClick={() => navigate("/login")} className="rounded-full brand-gradient-pink text-white border-0">Login</Button>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      {/* Mobile expandable search bar */}
      {mobileSearch && (
        <div className="md:hidden border-t border-slate-100 bg-background px-4 py-3" data-testid="mobile-search-bar">
          <form onSubmit={submitSearch} className="relative">
            <button type="submit" aria-label="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="w-4 h-4" />
            </button>
            <input
              type="text"
              autoFocus
              data-testid="mobile-search-input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search products…"
              className="w-full h-11 pl-11 pr-4 rounded-full bg-white border border-slate-200 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100 transition-all"
            />
          </form>
        </div>
      )}
    </header>
  );
}
